import { formatUnits } from 'viem';

const E18 = 10n ** 18n;

/** 18-decimal bigint → number (for display only; never feed it back into a transaction). */
export function toNum(v: bigint | undefined | null, decimals = 18): number {
  if (v === undefined || v === null) return NaN;
  return Number(formatUnits(v, decimals));
}

export function fmtNum(n: number, max = 2): string {
  if (!Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  if (abs >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (abs >= 1e4) return `${(n / 1e3).toFixed(1)}k`;
  const digits = abs > 0 && abs < 1 ? Math.min(6, max + 4) : max;
  return n.toLocaleString('en-US', { maximumFractionDigits: digits });
}

export function fmtToken(v: bigint | undefined | null, max = 4): string {
  return fmtNum(toNum(v), max);
}

/** Full figure with grouping (no k/M), for balances beside inputs where the exact amount matters. */
export function fmtBalance(v: bigint | undefined | null, maxFrac = 4): string {
  const n = toNum(v);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('en-US', { maximumFractionDigits: maxFrac });
}

export function fmtUsd(v: bigint | number | undefined | null): string {
  const n = typeof v === 'bigint' ? toNum(v) : v ?? NaN;
  if (!Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  if (abs >= 1e4) return `$${fmtNum(n)}`;
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: abs < 1 ? 4 : 2 })}`;
}

/** A token amount to a few significant figures, whatever its size: 0.685, 1.3, 1,234 (meters and sentences). */
export function fmtSig(v: bigint | undefined | null, digits = 4): string {
  const n = toNum(v);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('en-US', { maximumSignificantDigits: digits });
}

/** A network fee in BNB: four decimals once it is real money, two significant figures below that (0.00074). */
export function fmtFee(wei: bigint | undefined | null): string {
  const n = toNum(wei);
  if (!Number.isFinite(n)) return '—';
  if (n === 0) return '0';
  return n >= 0.01
    ? n.toLocaleString('en-US', { maximumFractionDigits: 4 })
    : n.toLocaleString('en-US', { maximumSignificantDigits: 2, maximumFractionDigits: 18 });
}

export function fmtPct(fraction: number, digits = 2): string {
  if (!Number.isFinite(fraction)) return '—';
  const pct = fraction * 100;
  if (Math.abs(pct) >= 1e6) return `${(pct / 1e6).toFixed(1)}M%`;
  if (Math.abs(pct) >= 1e4) return `${(pct / 1e3).toFixed(1)}k%`;
  return `${pct.toFixed(digits)}%`;
}

export function fmtBps(bps: bigint | number): string {
  return fmtPct(Number(bps) / 10_000);
}

export function fmtDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return '0s';
  const d = Math.floor(seconds / 86_400);
  const h = Math.floor((seconds % 86_400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

export function shortAddr(a: string | undefined): string {
  return a ? `${a.slice(0, 6)}…${a.slice(-4)}` : '';
}

/** An exact decimal string for an amount, for filling inputs (MAX, percentages). Never goes through a float. */
export function toInput(v: bigint, decimals = 18): string {
  const s = formatUnits(v, decimals);
  return s.includes('.') ? s.replace(/\.?0+$/, '') : s;
}

/** Parse a user-typed decimal string into 18-decimal units; null when it is not a valid non-negative number. */
export function parseAmount(input: string, decimals = 18): bigint | null {
  const s = input.trim();
  if (!/^\d*(\.\d*)?$/.test(s) || s === '' || s === '.') return null;
  const [whole, frac = ''] = s.split('.');
  if (frac.length > decimals) return null;
  return BigInt(whole || '0') * 10n ** BigInt(decimals) + BigInt((frac + '0'.repeat(decimals)).slice(0, decimals) || '0');
}

/** Per-epoch rate (1e18) → annual yield with two 12-hour epochs a day. */
export function apyFromRate(rate: bigint | undefined): number {
  if (rate === undefined) return NaN;
  return Math.pow(1 + Number(rate) / 1e18, 730) - 1;
}

export { E18 };
